import React from "react";
import { VLYT } from "@/constants/testIds/vlyt";
import useReveal from "@/hooks/useReveal";
import { Crown, Check, Zap } from "lucide-react";

export default function TheRoyalty({ onOpenWaitlist }) {
  const { ref, inView } = useReveal({ threshold: 0.2 });
  const perks = [
    "Guaranteed 8:00 AM pickup at your residence hall",
    "Priority matching within 4 minutes",
    "Locked pricing with no surge, ever",
    "Royal-only driver pool with a 4.7 star minimum",
    "Customized to your unique weekly routine",
  ];

  return (
    <section
      ref={ref}
      className="relative py-28 md:py-40 border-b border-[color:var(--vlyt-border)]"
      data-testid={VLYT.royalty.section}
    >
      <div className="mx-auto max-w-[1440px] px-5 md:px-10 grid lg:grid-cols-2 gap-16 items-center">
        <div className={`reveal ${inView ? "in" : ""}`}>
          <div className="text-eyebrow mb-5">03 · The Royalty</div>
          <h2 className="text-h1 font-display">
            Ride like <span className="text-lime">royalty.</span> Every morning.
          </h2>
          <p className="mt-6 text-[color:var(--vlyt-muted)] text-[16px] max-w-xl">
            The Royal Pass is engineered for the Kings and Queens of Hebron who value time as their
            primary asset. Secure a recurring daily pickup at your residence hall and move with the
            certainty of a future leader. Pricing is customized based on your unique weekly routine.
          </p>
          <ul className="mt-8 space-y-3">
            {perks.map((p) => (
              <li key={p} className="flex items-start gap-3">
                <span className="mt-1 h-5 w-5 rounded-full bg-[rgba(191,255,0,0.12)] flex items-center justify-center ring-1 ring-[color:var(--vlyt-lime)] shrink-0">
                  <Check size={12} className="text-lime" strokeWidth={3} />
                </span>
                <span className="text-[15px]">{p}</span>
              </li>
            ))}
          </ul>
          <button
            type="button"
            onClick={onOpenWaitlist}
            data-testid={VLYT.royalty.cta}
            className="btn-lime mt-10"
          >
            <Zap size={16} strokeWidth={2.8} /> Reserve your Royal seat
          </button>
        </div>

        <div className={`reveal ${inView ? "in" : ""}`} style={{ transitionDelay: "120ms" }}>
          <div
            className="mx-auto max-w-md"
            style={{ transform: "rotate(-3deg)" }}
          >
            <div className="shimmer-border">
              <div className="inner p-8 md:p-10" data-testid={VLYT.royalty.card}>
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-2 text-eyebrow">
                    <Crown size={14} className="text-lime" /> Royal Rides
                  </div>
                  <span className="font-mono text-[11px] px-2 py-1 rounded-full border border-[color:var(--vlyt-border)] text-[color:var(--vlyt-purple-soft)]">
                    Daily
                  </span>
                </div>

                <div className="font-display leading-none" data-testid={VLYT.royalty.priceLine}>
                  <div className="text-[14px] font-mono uppercase tracking-widest text-[color:var(--vlyt-muted)] mb-2">
                    The Royal Rides
                  </div>
                  <div className="text-[34px] md:text-[38px] text-lime tracking-tight leading-[1.05]">
                    Guaranteed 8:00 AM pickups
                  </div>
                  <div className="mt-2 text-[15px] text-[color:var(--vlyt-purple-soft)] font-mono">
                    or your selected time
                  </div>
                  <div className="mt-3 text-[14px] text-[color:var(--vlyt-muted)] font-mono">
                    Pricing customized to your weekly routine
                  </div>
                </div>

                <div className="h-px my-8 bg-[color:var(--vlyt-border)]" />

                <div className="space-y-3">
                  {perks.slice(0, 4).map((p) => (
                    <div key={p} className="flex items-center gap-3 text-[14px]">
                      <span className="h-1.5 w-1.5 rounded-full bg-lime shrink-0" />
                      <span>{p}</span>
                    </div>
                  ))}
                </div>

                <div className="mt-8 flex items-center gap-3 text-[12px] font-mono text-[color:var(--vlyt-muted)]">
                  <span className="text-lime">◆</span> Pauses on academic breaks. Cancel anytime.
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
